import { inject, Injectable, Type } from '@angular/core';
import { Observable, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { MDDialogService } from '@melodic-ui/components';
import { SubstituteInstructorDialogComponent } from './substitute-instructor-dialog.component';
import { IEnrollment } from '../../api/mylb/interfaces/ienrollment-summary.interface';
import { EnrollmentService } from '../../api/mylb/services/enrollment.service';
import { EnrollmentStateService } from '@lb/state/enrollment-state.service';

/**
 * Who currently teaches the lesson(s) the dialog was opened for — which is NOT necessarily the
 * enrollment's instructor. Once a lesson has been covered, its instructor is whoever sits on the
 * history row it points at, and the enrollment's own instructor is unchanged. The dialog showed the
 * enrollment's, so a lesson already covered by a substitute displayed the primary as its "current
 * instructor" and picking the real answer looked like a no-op.
 */
export interface ILessonInstructorContext {
	userID?: number;
	fullName?: string;
}

@Injectable({ providedIn: 'root' })
export class SubstituteInstructorDialogService {
	private _dialog = inject(MDDialogService);
	private _enrollmentStateService = inject(EnrollmentStateService);
	private _enrollmentService = inject(EnrollmentService);

	open(enrollment: IEnrollment, lessonIDs: number[], lessonInstructor?: ILessonInstructorContext) {
		return this._dialog.open(SubstituteInstructorDialogComponent as Type<any>, { enrollment, lessonIDs, lessonInstructor: lessonInstructor ?? null });
	}

	openByEnrollmentId(enrollmentID: number, lessonIDs: number[], lessonInstructor?: ILessonInstructorContext): Observable<any> {
		// Look up the enrollment from state first
		const activeEnrollments = this._enrollmentStateService.activeEnrollments();
		const inactiveEnrollments = this._enrollmentStateService.inactiveEnrollments();

		// Find the enrollment in active or inactive enrollments
		const enrollment = [...activeEnrollments, ...inactiveEnrollments].find((e) => e.enrollmentID === enrollmentID);

		if (enrollment) {
			// Found in state, open dialog immediately and return the dialog reference
			return this.open(enrollment, lessonIDs, lessonInstructor).afterClosed;
		} else {
			// Not in state, fetch from API then open dialog
			console.log('Enrollment not found in state, fetching from API for ID:', enrollmentID);
			return this._enrollmentService.getEnrollmentSummary(enrollmentID).pipe(
				switchMap((fetchedEnrollment) => {
					return this.open(fetchedEnrollment, lessonIDs, lessonInstructor).afterClosed;
				}),
				catchError((error) => {
					console.error('Failed to fetch enrollment:', error);
					// Return empty observable if enrollment fetch fails
					return EMPTY;
				})
			);
		}
	}
}
